import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _debounce from 'lodash/debounce';
import _uniqueId from 'lodash/uniqueId';
import { connect } from 'react-redux';

// import a bunch of old libs
import $ from 'jquery';
import 'at.js/dist/js/jquery.atwho';
import 'at.js/dist/css/jquery.atwho.css';

import { FontAwesome } from '../../FontAwesome';

import zenpen from '../../../vendor/zenpen/zenpen';
import { ImageTools } from '../../../utilities/image-tools';

import { noop } from '../../../utilities/functions';
import { mountTarget, unmountTarget } from '../../mountable';
import { makeSelectCommentPlaceholder } from '../../../../ReduxStorage/BlockPageUI/selectors';

import { ActionPromiseButton as ActionButton } from '../../Buttons/ActionButton/ActionPromiseButton';
import { CircleProfilePicture } from '../../CircleProfilePicture/CircleProfilePicture';
import { AttachFilesButton } from './AttachFilesButton';
import { DetailedImage } from '../../accessibility/DetailedImage/component';
import { DetailedImageAuthoringModal } from '../../accessibility/DetailedImageAuthoringModal/component';

import './ReplyForm.scss';
import { makeMetricsLogEvent } from '../../../../ReduxStorage/Metrics/manualEventDispatchers';

const ATTACHMENT_SIZES = {
  Large: { width: 800, height: 600 },
  Medium: { width: 600, height: 450 },
  Small: { width: 400, height: 300 },
};

class ReplyForm_ extends React.PureComponent {
  // majority of the prop names come from OLCommenting.context object
  static propTypes = {
    document: PropTypes.string.isRequired, // documentId of object
    resourceName: PropTypes.string, // optional because of legacy pages, I think
    parentComment: PropTypes.string, // commentId of parent comment
    groupId: PropTypes.string, // groupId if replying on a group context

    cancomment: PropTypes.bool.isRequired,
    create: PropTypes.bool,
    collapsed: PropTypes.bool,
    attachmentText: PropTypes.string.isRequired,

    indent: PropTypes.number.isRequired,
    placeholder: PropTypes.string,

    // legacy object doesn't match UserResource summary
    // don't think there is a non-resource user proptype
    // eslint-disable-next-line react/forbid-prop-types
    user: PropTypes.object,
    IMAGES_URL: PropTypes.string.isRequired, // legacy cruft

    // new options
    deactivateAfterPost: PropTypes.bool, // reset to !isActivated after posting

    onSubmit: PropTypes.func, // should return a promise
    onPost: PropTypes.func.isRequired, // when comment posted successfully
    onPostError: PropTypes.func, // on comment post error

    metricsLogEvent: PropTypes.func.isRequired,
  };

  static defaultProps = {
    resourceName: null,
    parentComment: null,
    groupId: null,

    create: false,
    collapsed: false,
    placeholder: 'Write a comment ...',

    user: {}, // not sure if this is entirely correct

    deactivateAfterPost: true,

    // eslint-disable-next-line no-unused-vars
    onSubmit: (formData) => {
      // TODO: return a default dispatcher rather than placeholder promise
      return new Promise((resolve, reject) => {
        reject(Error('Comment submit has not been implemented yet.'));
      });
    },
    onPostError: noop, // (errorMessage) => {}
  };

  constructor(props) {
    super(props);

    this.state = {
      // form only shows a 1 line textfield until user interacts with it
      // when true, it will show more options and an editable "textarea"
      // create = true for top level reply form, undefined for reply to comment
      isActivated: !props.create,
      isSubmitting: false,
      isPostSuccessful: false,
      errorMessage: null,

      showDetailsModal: false,
      imageAltText: '',
      imageLongDescription: '',
      imageCitationText: '',
      imageCitationLink: '',

      attachedFile: null,
      isImageFile: false,
      previewImage: false,
      resizeTo: 'Large',
      attachFilesKey: _uniqueId('attachFilesKey'),
    };

    this.commentToolbarRef = React.createRef();
    this.zenPenUrlInputId = _uniqueId('zen-pen-url-input-');
    this.replyFormInputId = _uniqueId('reply-form-input-');
  }

  onPlaceholderFocus = () => {
    this.setState(
      {
        isActivated: true,
      },
      this.onActivated
    );
  };

  onActivated = () => {
    const { isActivated } = this.state;

    if (isActivated) {
      setTimeout(() => {
        // form could be removed before this occurs
        if (this.commentEntry) {
          this.commentEntry.focus();
        }
      }, 0);
    }
  };

  refCommentEntry = (el) => {
    this.commentEntry = el;

    if (!el) {
      return;
    }

    // zenpen wysiwyg stuff
    if (zenpen.isSupported) {
      zenpen(this.commentEntry, this.commentToolbarRef.current);
    }

    // assumes contenteditable is already set
    this.initTagging();

    this.onActivated();
  };

  // initialise user tagging
  initTagging = () => {
    // TODO: this is mostly legacy code, can do with a cleanup
    const taggingRegex = /@([a-z0-9.-]{2,40}[^. ?!])/;

    $(this.commentEntry).atwho({
      at: '@',
      displayTpl: `<li data-value="\${atwho-at}\${id}" class="userProfileItem">
        <img src="\${icon}" height="24" width="24">
        <span class="highlightable">\${title}</span> <span class="profile highlightable">\${id}</span>
      </li>`,
      insertTpl: `\${atwho-at}\${id}`,
      max_len: 40,
      callbacks: {
        highlighter: (li, query) => {
          const regex = new RegExp(query, 'gi');
          const result = $(li)
            .find('.highlightable')
            .html((i, html) => {
              return html.replace(regex, '<strong>$&</strong>');
            })
            .end()
            .outerHTML();
          return result;
        },
        matcher: (flag, subtext) => {
          // flag, subtext, shouldStartWithSpace
          const match = subtext.match(taggingRegex, 'gi');
          return match && match[1];
        },
        remoteFilter: _debounce((query, callback) => {
          if (query.length >= 2) {
            const $this = $(this.commentEntry);
            const existingRequest = $this.data('suggest');

            if (existingRequest) {
              existingRequest.abort();
            }

            const jqXHR = $.getJSON(
              '/json/search/lookup/suggestUser/',
              {
                query,
                cohortPaths: JSON.stringify([
                  window.OpenLearningUserData.currentCohort,
                ]),
              },
              (data) => {
                callback(data.result.results);
              }
            );

            $this.data('suggest', jqXHR);
          }
        }, 500),
        sorter: (query, items) => {
          // query, items, searchKey
          return items; // already sorted on backend
        },
      },
    });
  };

  getFormData = () => {
    const { document, resourceName, parentComment, groupId } = this.props;

    return {
      document,
      resourceName,
      parentComment: parentComment || '',
      groupId: groupId || '',
      // Twig: for now we'll keep existing bugs...
      content: this.commentEntry.innerHTML.trim(),

      // Note: attachment data retrieved in getFormFileData()
    };
  };

  onSubmitClick = () => {
    return new Promise((resolve, reject) => {
      // one does not simply trim HTML
      // old code: $content.val($.trim(commentHTML)); // set (trimmed) HTML as comment content

      // This is a "sorta working" test, but won't strip whitespace from start/end when posting
      if (this.commentEntry.innerText.trim() === '') {
        reject(Error('You cannot post an empty comment.'));
        return;
      }

      this.setState({
        isSubmitting: true,
      });

      // prepare POST data
      const { onSubmit } = this.props;
      const commentData = new FormData();
      const formData = this.getFormData();

      Object.keys(formData).forEach((key) => {
        commentData.append(key, formData[key]);
      });

      this.getFormFileData().then((fileData) => {
        if (fileData) {
          Object.keys(fileData).forEach((key) => {
            commentData.append(key, fileData[key]);
          });
        }

        onSubmit(commentData)
          .then(resolve)
          .catch(reject);
      });
    });
  };

  onCommentPosted = (response) => {
    const { comment } = response;
    const { indent, onPost, deactivateAfterPost, metricsLogEvent } = this.props;

    // even when we succeed, we can fail
    if (!response.success) {
      this.onCommentError(
        response.message ||
          'Your comment could not be posted at this time. Please try again in a moment.'
      );

      // Twig: why would it be unsuccessful but create a comment anyway?
      if (comment) {
        metricsLogEvent('ErrorComment-Posted', {
          comment: comment.id,
          data: this.getFormData(),
          result: response,
        });
      }

      return;
    }

    // metrics
    let onObject = null;
    if (comment.documentType === 'collection') {
      onObject = 'Post';
    } else if (comment.documentType === 'page') {
      onObject = 'Page';
    }

    metricsLogEvent('Comment-Made', {
      comment: comment.id,
      depth: indent,
      onObject,
      appWidget: comment.documentType === 'collection' ? 'galleryWidget' : null,
    });

    // empty out commenting form
    this.commentEntry.innerHTML = '';

    // tidy up
    this.setState({
      isSubmitting: false,
      isPostSuccessful: true,
      errorMessage: null,

      isActivated: !deactivateAfterPost,

      attachedFile: null,
      isImageFile: false,
      attachFilesKey: _uniqueId('attachFilesKey'), // update key to force refresh

      imageAltText: '',
      imageLongDescription: '',
      imageCitationText: '',
      imageCitationLink: '',
    });

    onPost(comment, {
      indent,
    });
  };

  onCommentError = (error) => {
    const { onPostError, metricsLogEvent } = this.props;
    const errorMessage =
      (error ? error.message : '') ||
      'There may have been a problem posting this comment. Please re-load the page to see if it was posted.';

    this.setState({
      isSubmitting: false,
      isPostSuccessful: false,
      errorMessage,
    });

    metricsLogEvent('ErrorComment-Posted', {
      data: this.getFormData(),
    });

    onPostError(errorMessage);
  };

  renderPostingStatus = () => {
    const { isSubmitting, isPostSuccessful, errorMessage } = this.state;

    // Our action button already includes a spinner
    if (isSubmitting) {
      return null;
    }

    if (!isPostSuccessful && errorMessage) {
      return (
        <span className="text-error" aria-live="polite">
          <FontAwesome name="times" /> {errorMessage}
        </span>
      );
    }

    return null;
  };

  onResizeChange = (event) => {
    this.setState({
      resizeTo: event.currentTarget.value,
    });
  };

  renderImageResizeSelection = () => {
    const {
      resizeTo,
      attachedFile,
      previewImage,
      imageAltText,
      imageLongDescription,
      imageCitationText,
      imageCitationLink,
    } = this.state;
    const showDetailedImage = !!(
      imageLongDescription ||
      imageCitationText ||
      imageCitationLink
    );

    if (!previewImage) {
      return null;
    }

    let preview = null;
    const imageClassName = `size-${resizeTo.toLowerCase()}`;

    if (showDetailedImage) {
      preview = (
        <DetailedImage
          {...{
            imageClassName,
            src: previewImage,
            altText: imageAltText,
            longDescription: imageLongDescription,
            citationText: imageCitationText,
            citationLink: imageCitationLink,
          }}
        />
      );
    } else {
      preview = (
        <img
          className={imageClassName}
          src={previewImage}
          alt={imageAltText || `Preview of ${attachedFile.name}`}
        />
      );
    }

    return (
      <div className="image-attachment-size">
        <div>
          {'Upload Dimensions: '}
          <select value={resizeTo} onChange={this.onResizeChange}>
            {Object.keys(ATTACHMENT_SIZES).map((key) => {
              const info = ATTACHMENT_SIZES[key];

              return (
                <option value={key} key={key}>
                  {key} ({info.width} x {info.height}px)
                </option>
              );
            })}
          </select>
        </div>

        <div className="image-preview">{preview}</div>
      </div>
    );
  };

  renderImageDetailsInput = () => {
    const {
      showDetailsModal,
      attachedFile,
      previewImage,
      imageAltText,
      imageLongDescription,
      imageCitationText,
      imageCitationLink,
    } = this.state;

    const detailsModalProps = {
      defaultSrc: previewImage,
      defaultAltText: imageAltText,
      defaultLongDescription: imageLongDescription,
      defaultCitationText: imageCitationText,
      defaultCitationLink: imageCitationLink,

      hideRemoveButton: true,
      onSave: this.onDetailsModalSave,
      onClose: this.onDetailsModalClose,
    };

    return (
      <div className="image-details-wrap">
        <button
          type="button"
          disabled={!attachedFile}
          onClick={this.onEditDetailsClick}
          className="anchor-button"
        >
          Edit image details
        </button>

        {showDetailsModal && (
          <DetailedImageAuthoringModal {...detailsModalProps} />
        )}
      </div>
    );
  };

  onEditDetailsClick = () => {
    this.setState({
      showDetailsModal: true,
    });
  };

  onDetailsModalSave = (data) => {
    return new Promise((onSuccess) => {
      const { altText, longDescription, citationText, citationLink } = data;

      this.setState(
        {
          imageAltText: altText,
          imageLongDescription: longDescription,
          imageCitationText: citationText,
          imageCitationLink: citationLink,
        },
        onSuccess
      );
    });
  };

  onDetailsModalClose = () => {
    this.setState({
      showDetailsModal: false,
    });
  };

  getFormFileData = () => {
    // This is async, so we return a promise

    return new Promise((resolve) => {
      const { attachedFile, isImageFile, resizeTo } = this.state;
      const {
        imageAltText,
        imageLongDescription,
        imageCitationText,
        imageCitationLink,
      } = this.state;

      // no file attached
      if (!attachedFile) {
        resolve(null);
        return;
      }

      const data = {
        'comment-attachment': attachedFile,
      };

      // not image, can't resize
      if (!isImageFile) {
        resolve(data);
        return;
      }

      Object.assign(data, {
        imageAltText,
        imageLongDescription,
        imageCitationText,
        imageCitationLink,
      });

      // TODO: don't use ImageTools anymore - memleaks
      // attempt to resize image before upload
      ImageTools.resize(attachedFile, resizeTo, (file, result) => {
        if (result.status === 'unsupported' || !result.success) {
          // fallback to do the resize on the server
          Object.assign(data, {
            resize: 1,
            'attachment-size': resizeTo,
          });

          resolve(data);
          return;
        }

        // add the file name to the blob
        // eslint-disable-next-line no-param-reassign
        file.name = attachedFile.name;

        // cleanup the object url after upload
        // var objectURL = $(image).attr('src');

        // postComment.always(function(jqXHR, textStatus, errorThrown) {
        //   revokeObjectURL(objectURL);
        // });

        // resize success
        Object.assign(data, {
          'comment-attachment': file,
        });

        resolve(data);
      });
    });
  };

  onFileChange = (files) => {
    let isImageFile = false;
    let attachedFile = null;

    // for now only allow 1 attachment
    if (files.length > 0) {
      attachedFile = files[0];
      isImageFile =
        attachedFile.type.startsWith('image/') &&
        ImageTools.isImage(attachedFile);
    }

    if (isImageFile) {
      const reader = new FileReader();

      reader.onload = () => {
        this.setState({
          previewImage: reader.result,
        });
      };

      reader.readAsDataURL(attachedFile);
    }

    this.setState({
      attachedFile,
      isImageFile,
      previewImage: null,
      resizeTo: 'Large', // it never upscales
    });
  };

  renderZenPenToolbar = () => {
    // needs to be a child of document.body
    const content = (
      <div
        ref={this.commentToolbarRef}
        className="text-options zenpen-comment-toolbar"
        aria-label="Comment formatting toolbar"
      >
        <div className="options">
          <span className="no-overflow">
            <span className="lengthen ui-inputs">
              <button type="button" className="url">
                &#xf0c1;
              </button>
              <label
                className="zenpen-url-input-label"
                htmlFor={this.zenPenUrlInputId}
              >
                URL
              </label>
              <input
                className="url-input"
                type="url"
                placeholder="Type or Paste URL here"
                id={this.zenPenUrlInputId}
              />
              <button type="button" className="bold">
                b
              </button>
              <button type="button" className="italic">
                i
              </button>
              <button type="button" className="underline">
                U
              </button>
              <button type="button" className="quote">
                &rdquo;
              </button>
            </span>
          </span>
        </div>
      </div>
    );

    return ReactDOM.createPortal(content, document.body);
  };

  render() {
    const {
      cancomment,
      indent,
      create,
      collapsed,
      user,
      placeholder,
      attachmentText,
      IMAGES_URL,
    } = this.props;
    const {
      isActivated,
      attachFilesKey,
      attachedFile,
      isImageFile,
    } = this.state;

    if (!cancomment && indent === 0) {
      return null;
    }

    const className = classNames(
      'comment-container',
      'comment-container-reply',
      {
        write: create,
        reply: !create,
        collapsed: create || (!create && collapsed),
        indented: indent > 0,
        [`indent-level-${indent}`]: indent > 0,
      }
    );

    return (
      <reply-form class={className} data-indent={indent}>
        {!isActivated && (
          <div className="flex-wrap-horizontal">
            <label
              className="placeholder-input-label"
              htmlFor={this.replyFormInputId}
            >
              {placeholder}
            </label>
            <input
              type="text"
              placeholder={placeholder}
              onFocus={this.onPlaceholderFocus}
              className="placeholder-input"
              id={this.replyFormInputId}
            />
          </div>
        )}

        {isActivated && (
          <div className="flex-wrap-vertical">
            <div className="flex-wrap-horizontal">
              <div>
                <CircleProfilePicture
                  altText={user.profileName}
                  imageUrl={user.avatar48 || `${IMAGES_URL}profile/0.png`}
                />
              </div>
              <div
                className="comment-entry"
                contentEditable
                role="textbox"
                aria-label="Write a comment"
                aria-placeholder="Write a comment"
                ref={this.refCommentEntry}
              />
            </div>

            {this.renderPostingStatus()}

            <AttachFilesButton
              key={attachFilesKey}
              onChange={this.onFileChange}
              attachLabel={attachedFile ? attachedFile.name : attachmentText}
            />
            {attachedFile && isImageFile && this.renderImageResizeSelection()}
            {attachedFile && isImageFile && this.renderImageDetailsInput()}

            <div className="submit-wrap">
              <ActionButton
                label="Post Comment"
                colour="orange"
                onClick={this.onSubmitClick}
                onSuccess={this.onCommentPosted}
                onError={this.onCommentError}
              />
            </div>
          </div>
        )}

        {this.renderZenPenToolbar()}
      </reply-form>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { parentComment, resourceName, document } = props;
  let { placeholder } = props;

  if (!parentComment && resourceName === 'page' && document) {
    // Only select the commentPlaceholder for the top level page comment
    placeholder = makeSelectCommentPlaceholder(document)(state);
  }

  return {
    placeholder,
  };
};

export const ReplyForm = connect(
  mapStateToProps,
  {
    metricsLogEvent: makeMetricsLogEvent,
  }
)(ReplyForm_);

// export these as helpers to legacy code
export const mount = (target, props) => mountTarget(ReplyForm, target, props);
export const unmount = unmountTarget;
